<template>
  <b-container
    class="square-transaction-terminal-payment-wrapper"
    data-cy="terminal-payment-component"
  >
    <square-payments-response-modals
      :success-component="successComponent"
      :failure-component="failureComponent"
      :error-component="errorComponent"
      :debug="debugPayment"
      @closed="completeModalClose"
    />

    <square-payments-debug-info
      v-if="debugPayment && !loading"
      :debug-info="debugInfo"
    />

    <div v-if="paymentInitError">
      <b-modal
        id="payment-init-error"
        v-model="showPaymentInitError"
        hide-header
        no-close-on-esc
        ok-only
        size="lg"
        static
        @ok="errorModalClosed"
      >
        <square-payments-payment-init-error
          :error="paymentInitError"
        />
      </b-modal>
    </div>

    <b-row
      v-if="!isAnonymous && !ignoreUserStoredCards && showStoredCardSelection"
      v-show="!loading"
      class="mb-3"
    >
      <b-col>
        <square-payments-stored-card-select
          v-model="selectedCard"
          :account="account"
          :allow-other="true"
          :disabled="processing || processingComplete"
          @error="usercardLoadingError"
          @loading="usercardSelectStatusHandler"
          @ready="storedCardsReadyHandler"
        >
          <template v-slot:stored-card-header>
            <slot name="stored-card-header">
              <h3>
                Select from the stored credit cards linked to your account.
              </h3>
            </slot>
          </template>
          <template v-slot:stored-card-message>
            <slot name="stored-card-message">
              Select from the stored credit cards linked to your account.
            </slot>
          </template>
          <template v-slot:enter-new-card-label>
            <slot name="enter-new-card-label">
              Square Terminal
            </slot>
          </template>
        </square-payments-stored-card-select>
      </b-col>
    </b-row>

    <b-row v-if="processing && !cancelling && !showTimeoutModal">
      <b-col data-cy="processing-payment-spinner">
        <loading-spinner>
          <div slot="title">
            <slot name="processing-title">
              Processing Payment
            </slot>
          </div>
          <div slot="message">
            <slot name="processing-message">
              Processing your payment with Square Terminal...
            </slot>
          </div>
        </loading-spinner>
        <square-terminal-cancel-payment-request @cancelled="onCancelPayment" />
      </b-col>
    </b-row>

    <b-row v-if="processing && !cancelling && showTimeoutModal">
      <b-col data-cy="payment-timeout-options">
        <div class="payment-timeout-container">
          <div class="timeout-header">
            <font-awesome-icon
              icon="clock"
              size="3x"
              class="text-warning mb-3"
            />
            <h4 class="mb-3">
              Payment Response Not Received
            </h4>
          </div>

          <div class="timeout-body">
            <p class="timeout-message">
              The payment response from the Square terminal has not been received after {{ timeoutElapsedTime }} seconds.
            </p>

            <p class="instruction-message">
              Please select one of the following options:
            </p>

            <div
              v-if="waitingForRetry"
              class="retry-info mb-4"
            >
              <b-spinner
                small
                class="mr-2"
              />
              <span>Waiting for response... ({{ remainingWaitTime }}s remaining)</span>
            </div>

            <div class="action-buttons-row">
              <b-button
                :disabled="waitingForRetry || manualConfirmPending"
                variant="primary"
                size="lg"
                class="action-button"
                @click="handleManualConfirm"
              >
                <font-awesome-icon
                  icon="check-circle"
                  class="mr-2"
                />
                Manually Confirm Payment
              </b-button>

              <b-button
                :disabled="waitingForRetry || manualConfirmPending"
                variant="success"
                size="lg"
                class="action-button"
                @click="handleContinueWaiting"
              >
                <font-awesome-icon
                  icon="hourglass-half"
                  class="mr-2"
                />
                Continue Waiting (30s)
              </b-button>

              <b-button
                :disabled="waitingForRetry || manualConfirmPending"
                variant="danger"
                size="lg"
                class="action-button"
                @click="handleCancelPaymentFromTimeout"
              >
                <font-awesome-icon
                  icon="times-circle"
                  class="mr-2"
                />
                Cancel Payment
              </b-button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="processing && cancelling">
      <b-col data-cy="cancelling-payment-spinner">
        <loading-spinner>
          <div slot="title">
            <slot name="cancelling-title">
              Cancelling Payment
            </slot>
          </div>
          <div slot="message">
            <slot name="cancelling-message">
              Cancelling your payment request...
            </slot>
          </div>
        </loading-spinner>
      </b-col>
    </b-row>

    <b-row
      v-show="!loading && !processing && !processingComplete && useSquareTerminal"
      class="square-terminal-payment-element"
      :class="{ 'disabled': testMode && isPaymentAmountTooHigh }"
    >
      <b-col>
        <!-- Test Mode Warning -->
        <div
          v-if="testMode"
          class="test-mode-warning mb-3"
        >
          <div class="alert alert-warning d-flex align-items-center">
            <font-awesome-icon
              icon="triangle-exclamation"
              class="warning-icon me-2"
              size="lg"
            />
            <div>
              <strong>TestMode:</strong> Terminal Payments in test mode are limited to $25. Payment request over $25 will not be processed correctly. To test different Terminal Payment responses you need to select one of the available test device IDs from the connection manager.
            </div>
          </div>
        </div>

        <square-terminal-payment-button
          v-model="storeCreditCard"
          :userid="userid"
          :disabled="processing || !showPaymentButton || isPaymentAmountTooHigh"
          :class="{ 'payment-amount-too-high': testMode && isPaymentAmountTooHigh }"
          :enable-user-card-storage="enableUserCardStorage"
          form-type="Payments"
          data-cy="terminal-payment-button"
          @click="processTerminalPayment"
        >
          <slot>
            <div class="payment-button-content">
              <span class="amount">Pay {{ displayAmount | formatCurrency(currency) }}</span>
              <div
                v-if="displaySurcharge"
                class="surcharge-message"
              >
                Payment amount includes a surcharge of {{ surchargeAmount | formatCurrency(currency) }}
              </div>
            </div>
          </slot>
        </square-terminal-payment-button>
      </b-col>
    </b-row>

    <!-- Payment button for stored card payments -->
    <b-row v-show="!loading && !processing && !processingComplete && !useSquareTerminal && showStoredCardPaymentButton">
      <b-col>
        <payment-button
          data-cy="stored-card-payment-button"
          :disabled="processing || !showPaymentButton"
          form-type="Payments"
          @click="processStoredCardPayment"
        >
          <slot>
            <div class="payment-button-content">
              <div class="amount">
                Pay {{ displayAmount | formatCurrency(currency) }}
              </div>
              <div
                v-if="displaySurcharge"
                class="surcharge-message"
              >
                Payment amount includes a surcharge of {{ surchargeAmount | formatCurrency(currency) }}
              </div>
            </div>
          </slot>
        </payment-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SquarePaymentsMixin from '../mixins/square-payments'
import SquareTerminalPaymentButton from './terminal-payment-button.vue'
import PaymentButton from '../cards/card-payment-button.vue'
import SquarePaymentResponseModals from '../modals/response-modals.vue'
import SquarePaymentInitError from '../modals/content/payment-init-error.vue'
import SquareTerminalCanclePaymentRequest from './SquareTerminalPaymentCancelPayment.vue'
import UserStoredCardSelect from '../components/UserStoredCardSelect.vue'
import SquarePaymentsDebugInfo from '../debug/SquarePaymentsDebugInfo.vue'

export default {
  name: 'square-payments-terminal-payment',
  components: {
    'square-payments-response-modals': SquarePaymentResponseModals,
    'square-terminal-payment-button': SquareTerminalPaymentButton,
    'payment-button': PaymentButton,
    'square-payments-payment-init-error': SquarePaymentInitError,
    'square-payments-stored-card-select': UserStoredCardSelect,
    'square-terminal-cancel-payment-request': SquareTerminalCanclePaymentRequest,
    'square-payments-debug-info': SquarePaymentsDebugInfo
  },
  mixins: [
    SquarePaymentsMixin
  ],
  props: {
    txn: {
      type: Object,
      required: true
    },
    amount: {
      type: [Number, String],
      required: true
    },
    account: {
      type: Object,
      required: true
    },
    successComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    failureComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    errorComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        debug: false,
        displaySurcharge: false,
        removeStoreCardButton: false,
        defaultCardholderName: '',
        ignoreUserStoredCards: false,
        enableUserCardStorage: true,
        defaultUserCardStorage: true,
        requestOptions: {},
        storeUserCardDefault: false,
        paymentMethods: {}
      })
    }
  },
  data: function() {
    return {
      elements: null,
      storeCreditCard: false,
      loading: false,
      processing: false,
      cancelling: false,
      selectedCard: 'new',
      loadingStoredCards: false,
      showPaymentButtonVal: false,
      processingComplete: false,
      paymentRequest: null,
      storedCardsReady: false, // Track if stored cards component is ready
      showButtonDelayTimer: null, // Timer for button display delay
      showTimeoutModal: false,
      timeoutElapsedTime: 0,
      paymentCancelFunction: null,
      extendWaitFunction: null,
      manualConfirmPending: false,
      waitingForRetry: false,
      remainingWaitTime: 30,
      countdownInterval: null
    }
  },
  computed: {
    userid: function() {
      return this.account ? this.account.userid.toString() : '0'
    },
    txnid: function() {
      return this.txn && this.txn.txnid
    },
    useSquareTerminal: function() {
      return this.selectedCard === 'new'
    },
    showNewCardEntry: function() {
      return !!(this.selectedCard === 'new') && !this.showCardSourceIDOverride
    },
    showStoreNewCardOption: function() {
      return !this.isAnonymous && this.showNewCardEntry && !this.showCardSourceIDOverride && !this.loading
    },
    ignoreUserStoredCards: function() {
      if (Object.prototype.hasOwnProperty.call(this.options, 'ignoreUserStoredCards') && this.options.ignoreUserStoredCards) {
        return this.options.ignoreUserStoredCards
      }
      return false
    },
    enableUserCardStorage: function() {
      if (Object.prototype.hasOwnProperty.call(this.options, 'enableUserCardStorage')) {
        return this.options.enableUserCardStorage
      }
      return true
    },
    defaultUserCardStorage: function() {
      if (Object.prototype.hasOwnProperty.call(this.options, 'defaultUserCardStorage') && this.options.enableUserCardStorage) {
        return this.options.defaultUserCardStorage
      }
      return true
    },
    showPaymentButton: function() {
      // For non-anonymous users with stored cards enabled, ensure stored cards are ready first
      if (!this.isAnonymous && !this.ignoreUserStoredCards) {
        // Don't show button if stored cards are still loading
        if (this.loadingStoredCards) {
          return false
        }
        // Don't show button until stored cards component has been initialized
        if (!this.storedCardsReady) {
          return false
        }
      }

      if (!this.processing && !this.loading) {
        if (this.isAnonymous) {
          return this.showPaymentButtonVal
        }
        else if (this.showNewCardEntry) {
          return this.showPaymentButtonVal
        }
        else {
          return true
        }
      }
      return false
    },
    showStoredCardPaymentButton: function() {
      // Show the stored card payment button when a stored card is selected
      return !this.isAnonymous && this.selectedCard !== 'new' && this.selectedCard && this.showPaymentButton
    },
    showStoredCardSelection: function() {
      // Hide stored card selection after successful payment completion
      // Check if payment was successful by looking at the processing result
      const processingResult = this.$store.getters['square/processingResult']
      const isSuccessfulPayment = processingResult &&
                                 processingResult.status &&
                                 processingResult.type === 'payment'

      // Hide the stored card selection if payment was successful
      return !isSuccessfulPayment
    },
    debugPayment: function() {
      try {
        if (this.testMode) {
          return !!this.options.requestOptions.debug
        }
        return false
      }
      catch (err) {
        return false
      }
    },
    displayAmount: function() {
      const amount = parseFloat(this.amount) + this.surchargeAmount
      return amount.toFixed(2)
    },
    paymentInitError: function() {
      return this.$store.state.square.paymentInitError
    },
    showPaymentInitError: {
      get: function() {
        return !!this.$store.state.square.paymentInitError
      },
      set: function(val) {
      }
    },
    showCardSourceIDOverride: function() {
      return !!(this.testMode && this.options.overrideSourceID && this.options.overrideSourceID.value !== 'default') && !this.loading
    },
    surchargeAmount: function() {
      if (this.paymentRequest && this.paymentRequest.surcharge > 0) {
        return this.paymentRequest.surcharge
      }
      return 0
    },
    displaySurcharge: function() {
      if (this.options.displaySurcharge && this.paymentRequest && this.paymentRequest.surcharge > 0) {
        return this.options.displaySurcharge
      }
      return false
    },
    isPaymentAmountTooHigh: function() {
      return this.testMode && parseFloat(this.amount) > 25
    },
    paymentIntentType: function() {
      if (this.storeCreditCard && this.enableUserCardStorage && this.selectedCard === 'new') {
        return 'CHARGE_AND_STORE'
      }
      return 'CHARGE'
    },
    customerInitiated: function() {
      return true
    },
    sellerKeyedIn: function() {
      return false
    },
    verificationDetails: function() {
      if (!this.txn || !this.txn.address) {
        return {}
      }
      return {
        amount: typeof this.txn.gross === 'number' && this.txn.gross > 0 ? this.txn.gross.toFixed(2) : '1.00',
        billingContact: Object.entries({
          givenName: this.txn.address.billing.firstname || null,
          familyName: this.txn.address.billing.lastname || null,
          email: this.txn.email || null,
          phone: this.txn.address.shipping.phone || null,
          addressLines:
                this.txn.address !== undefined
                  ? [
                    this.txn.address.billing.street1 || null,
                    this.txn.address.billing.street2 || null
                  ].filter(_ => _)
                  : [],
          city: this.txn.address !== undefined ? this.txn.address.billing.city || null : null,
          state: this.txn.address !== undefined ? this.txn.address.billing.state || null : null,
          countryCode: this.txn.address !== undefined ? this.txn.address.billing.country.toUpperCase() || null : null,
          postalCode: this.txn.address !== undefined ? this.txn.address.billing.postal_code || null : null
        }).reduce((acc, [key, value]) => value ? { ...acc, [key]: value } : acc, {}),
        currencyCode: (this.$store.getters['square/dflt_currency'] || 'AUD').toUpperCase(),
        intent: this.paymentIntentType,
        customerInitiated: this.customerInitiated,
        sellerKeyedIn: this.sellerKeyedIn
      }
    },
    debugInfo: function() {
      return {
        txnid: this.txnid,
        amount: this.amount,
        paymentRequestId: this.paymentRequest ? this.paymentRequest.id : '--',
        userid: this.userid,
        selectedCard: this.selectedCard,
        storeCreditCard: this.storeCreditCard,
        enableUserCardStorage: this.enableUserCardStorage,
        paymentIntentType: this.paymentIntentType,
        customerInitiated: this.customerInitiated,
        sellerKeyedIn: this.sellerKeyedIn,
        verificationDetails: this.verificationDetails,
        debugPayment: this.debugPayment,
        surchargeAmount: this.surchargeAmount
      }
    }
  },
  watch: {
    txnid: function() {
      if (!this.$store.getters.AcceleratedTransactions) {
        this.init()
      }
    },
    userid: function(val) {
      // this.$store.commit('square/STORE_USER_CARD')
      this.$store.commit('square/USERID', val)
    }
  },
  mounted: function() {
    this.$store.commit('square/PROCESSING_RESULT')
    this.processing = false
    this.processingComplete = false
    this.init()

    // For anonymous users, set storedCardsReady to true immediately
    // since they don't have stored cards to load
    if (this.isAnonymous || this.ignoreUserStoredCards) {
      this.storedCardsReady = true
      this.showPaymentButtonVal = true
    }
  },
  beforeDestroy() {
    // Clean up the delay timer if it exists
    if (this.showButtonDelayTimer) {
      clearTimeout(this.showButtonDelayTimer)
      this.showButtonDelayTimer = null
    }
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  },
  methods: {
    usercardSelectStatusHandler: function(val) {
      this.updateLoadingState({ loadingStoredCards: val })
    },
    storedCardsReadyHandler: function(readyData) {
      console.log('Stored cards ready:', readyData)

      // Clear any existing delay timer
      if (this.showButtonDelayTimer) {
        clearTimeout(this.showButtonDelayTimer)
        this.showButtonDelayTimer = null
      }

      // Add a small delay before showing the terminal payment button
      // This ensures a smooth transition after stored cards are displayed
      this.showButtonDelayTimer = setTimeout(() => {
        this.storedCardsReady = true
        this.showPaymentButtonVal = true
        this.showButtonDelayTimer = null
      }, 300) // 300ms delay for smooth UX
    },
    updateLoadingState: function(options) {
      Object.entries(options).forEach(([k, v]) => {
        this[k] = v
      })
      this.$emit('loading', (this.loadingStoredCards || this.loading))
    },
    init: function() {
      this.updateLoadingState({ loading: true })

      return this.$store.dispatch('square/cancelTerminalPaymentRequest')
        .catch(_err => {
        // No-op
        })
        .then(_ => {
          return this.$store.dispatch('square/initTerminalPayment', {
            txn: this.txn,
            userid: this.userid,
            amount: this.amount,
            paymentOptions: {
              ...this.options,
              storeCard: this.storeCreditCard,
              card_present: true
            }
          })
        })
        .then(paymentRequest => {
          this.paymentRequest = paymentRequest
          // Store the payment request in the store so computed properties can access surcharge data
          this.$store.commit('square/PAYMENT_DETAILS', paymentRequest)
          if (this.$store.getters['square/testMode']) {
            console.debug(`Square Payments: Initialize Card: App ID: ${this.appID} - Location ID: ${this.locationID}`)
          }
          this.$emit('connected', true)
        })
        .then(_ => {
          this.updateLoadingState({ loading: false })
        })
        .catch(err => {
          this.handleErrorResponse(err)
        })
    },
    usercardLoadingError: function(err) {
      this.paymentInitError = err
      this.handleErrorResponse(err)
    },
    errorModalClosed: function() {
      this.$store.commit('square/PAYMENT_INIT_ERROR')
    },
    completeModalClose: function(wasSuccessful) {
      // Check if this was a payment failure - if so, reinitialize the form for retry
      const processingResult = this.$store.getters['square/processingResult']
      const isPaymentFailure = processingResult &&
                              processingResult.type === 'payment' &&
                              !processingResult.status &&
                              processingResult.payment &&
                              ['CANCELED', 'FAILED'].includes(processingResult.payment.status)

      if (isPaymentFailure && !wasSuccessful) {
        // Clear the processing result and reinitialize the form for retry
        this.$store.commit('square/PROCESSING_RESULT')
        this.processing = false
        this.cancelling = false // Reset cancelling state
        this.processingComplete = false
        // Reinitialize the payment form to allow re-entry
        this.init()
      }
      else {
        // For successful payments or error cases, mark processing as complete
        // and hide the form elements
        this.processingComplete = true
        this.cancelling = false // Reset cancelling state
        this.$emit('completed', this.processingResult)
      }
    },
    handleErrorResponse: function(err) {
      this.updateLoadingState({ loading: false })
      this.$emit('error', err)
      this.processing = false
    },
    onCancelPayment: function(response) {
      this.cancelling = true
    },
    processStoredCardPayment: async function(recaptchaResponse) {
      if (this.debounceTimer !== null) {
        return
      }
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null
      }, 1500)

      this.processing = true
      let verified = true
      if (this.$store.getters['square/simulatedRecaptchaResponse'] !== null) {
        recaptchaResponse = this.$store.getters['square/simulatedRecaptchaResponse']
      }
      if (recaptchaResponse !== undefined) {
        verified = await this.$store.dispatch('square/verifyRecaptchaResponse', recaptchaResponse)
      }

      if (!verified) {
        this.$emit('recaptcha-error')
      }

      try {
        this.$emit('processing', true)
        this.processing = true

        const paymentRequest = {
          paymentRequestID: this.paymentRequest.id,
          cardSource: this.cardSource,
          storeCard: this.storeCreditCard,
          userid: this.userid,
          verificationDetails: this.paymentRequest.verificationDetails,
          security: {
            recaptcha: recaptchaResponse
          }
        }
        if (this.debugPayment) {
          console.debug(`SquarePaymentRequest: ${JSON.stringify(paymentRequest, null, 2)}`)
        }
        const response = await this.$store.dispatch('square/processPayment', paymentRequest)
        this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', ...response })
        if (response.status) {
          this.$emit('success', response.payment)
        }
        else if (!response.status && response.payment.status === 'FAILED') {
          this.$emit('failed', response)
        }
        else {
          this.$emit('error', response)
        }
        this.processingComplete = true
        this.$emit('processing', false)
        this.processing = false
      }
      catch (err) {
        // Ensure processing state is properly reset on any error
        this.$emit('processing', false)
        this.processing = false
        this.processingComplete = false

        if (err.name === 'SquareCardTokenizationError') {
          this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', status: false, error: err })
        }
        else {
          if (!this.$store.getters['square/processingResult']) {
            this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', status: false, error: err })
          }
        }

        // Emit error event to parent component
        this.$emit('error', err)
      }
    },
    handleManualConfirm: function() {
      // User confirmed payment was successful on terminal
      this.manualConfirmPending = true

      // Cancel the ongoing retry process
      if (this.paymentCancelFunction) {
        this.paymentCancelFunction()
      }

      // Close the modal
      this.showTimeoutModal = false

      // Create payment response object for manual confirmation
      const paymentResponse = {
        paymentIntent: {
          id: this.paymentRequest.id,
          created: Date.now() / 1000, // Unix timestamp in seconds
          amount: parseFloat(this.amount) * 100 // Amount in cents
        }
      }

      // Emit manually-confirm-payment event with the payment response
      // This will be handled by the parent component (POS checkout) which has the ManuallyConfirmPayment mixin
      this.$emit('manually-confirm-payment', paymentResponse)

      // Reset processing state
      this.processingComplete = false
      this.$emit('processing', false)
      this.processing = false
      this.manualConfirmPending = false
    },
    handleContinueWaiting: function(waitData) {
      // User chose to wait another 30 seconds - hide timeout and show processing spinner again
      this.showTimeoutModal = false
      this.waitingForRetry = false

      // Clear any existing countdown
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }

      if (this.extendWaitFunction && typeof this.extendWaitFunction === 'function') {
        this.extendWaitFunction(30000) // Extend by 30 seconds
      }
    },
    handleCancelPaymentFromTimeout: function() {
      // User chose to cancel the payment
      if (this.paymentCancelFunction) {
        this.paymentCancelFunction()
      }

      // Close the modal
      this.showTimeoutModal = false

      // Reset processing state
      this.processing = false
      this.processingComplete = false
      this.$emit('processing', false)

      // Create an error/cancelled result
      const cancelledResult = {
        status: false,
        payment: {
          status: 'CANCELLED',
          error: 'Payment cancelled by operator due to timeout'
        }
      }

      this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', ...cancelledResult })

      // Cancel the terminal payment request
      this.$store.dispatch('square/cancelTerminalPaymentRequest')
        .then(() => {
          this.$bvToast.toast('Payment processing has been cancelled. Please restart the payment if needed.', {
            title: 'Payment Cancelled',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        })
        .catch(err => {
          console.error('Error cancelling terminal payment:', err)
        })

      this.$emit('error', new Error('Payment cancelled by operator due to timeout'))
    }
  }
}
</script>

<style lang="scss">
  $btn-primary-color: rgb(12, 69, 161);
  $btn-success-color: rgb(69, 194, 78);
  $btn-danger-color: rgb(241, 68, 68);

  .payment-debug-info {
    border: 1px solid black;
    background: #eee;
  }

  .store-user-credit-card {
    margin: 0 1.6rem;
    .custom-control-label {
      padding-left: 0.75rem;
    }
  }

  .test-mode-warning {
    .alert {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      padding: 0.75rem 1.25rem;
      border-radius: 0.375rem;
    }

    .warning-icon {
      flex-shrink: 0;
      margin-right: 0.5rem;
      color: #856404;
    }
  }

  .payment-button-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .amount {
      font-size: 1.1rem;
      font-weight: 600;
    }

    .surcharge-message {
      font-size: 0.875rem;
      margin-top: 0.25rem;
      opacity: 0.8;
      font-style: italic;
    }
  }

  // Red border only when amount is too high in test mode
  .payment-amount-too-high {
    opacity: 0.6;

    .square-payment-button {
      border: 2px solid #dc3545;
      background: rgba(246, 170, 176, 0.75);
      border-radius: 0.375rem;
    }
  }

  // Disabled payment button cursor styles
  .square-terminal-payment-element.disabled {

    cursor: not-allowed !important;

    // Also apply not-allowed cursor to child elements
    * {
      cursor: not-allowed !important;
    }
  }

  // Payment timeout container styling
  .payment-timeout-container {
    text-align: center;
    padding: 2rem 1rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    margin: 1rem 0;

    .timeout-header {
      margin-bottom: 1.5rem;

      h4 {
        color: #212529;
        font-weight: 600;
      }
    }

    .timeout-body {
      .timeout-message {
        font-size: 1.1rem;
        margin-bottom: 1rem;
        color: #495057;
      }

      .instruction-message {
        font-weight: 600;
        margin-bottom: 1.5rem;
        color: #212529;
      }

      .retry-info {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        background-color: #f8f9fa;
        border-radius: 0.25rem;
        font-weight: 500;
        color: #212529;
      }

      .action-buttons-row {
        display: flex;
        justify-content: center;
        gap: 1rem;
        flex-wrap: wrap;
        margin-top: 2rem;

        .action-button {
          min-width: 200px;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;

          // Override Bootstrap button colors with custom colors
          &.btn-primary {
            background-color: $btn-primary-color;
            border-color: $btn-primary-color;

            &:hover:not(:disabled) {
              background-color: darken($btn-primary-color, 10%);
              border-color: darken($btn-primary-color, 10%);
            }
          }

          &.btn-success {
            background-color: $btn-success-color;
            border-color: $btn-success-color;

            &:hover:not(:disabled) {
              background-color: darken($btn-success-color, 10%);
              border-color: darken($btn-success-color, 10%);
            }
          }

          &.btn-danger {
            background-color: $btn-danger-color;
            border-color: $btn-danger-color;

            &:hover:not(:disabled) {
              background-color: darken($btn-danger-color, 10%);
              border-color: darken($btn-danger-color, 10%);
            }
          }
        }
      }
    }
  }
</style>
